import {
    Badge,
    Box,
    Button,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import {
    SearchableSelect,
    SearchableSelectCreatable,
    SelectCreatable,
} from '../../../common';
import {
    getUserVerticals,
    getUsers,
    getVerticals,
} from '../../../../http/apiClient';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OFFER_CATEGORIES } from '../../../common/enums/enums';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import { toGroupedOptions } from '../../../../utils/enumUtils';
import useAddMediaGroup from '../../../../http/mutations/groups/useAddMediaGroup';
import { useAuthStore } from '../../../../store';
import { useEffect } from 'react';
import { useForm } from '@mantine/form';

const CreateMediaGroupModal = ({ existing, opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {},

        validate: {
            name: (value) => {
                const { type, message } = genericValidator(
                    'Group name',
                    value,
                    {
                        required: true,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Group name must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            category: (value) => {
                const { message } = genericValidator('Category', value, {
                    required: true,
                });
                return message;
            },
            vertical: (value) => {
                const { type, message } = genericValidator('Vertical', value, {
                    required: false,
                    minLength: 2,
                    maxLength: 60,
                    pattern: /^[a-zA-Z0-9_-]+$/,
                });
                if (type) {
                    return type === 'pattern'
                        ? 'Vertical must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },

            trackingId: (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },

            tags: (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        form.setInitialValues({
            id: existing?.id.toString(),
            name: existing?.name || '',
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            category: existing?.category || '',
            vertical: existing?.vertical || '',
            trackingId: existing?.trackingId || '',
        });
        form.reset();
    }, [existing, opened]);

    const { isPending, mutate: addOrUpdate } = useAddMediaGroup();

    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>
                        {existing ? 'Update' : 'Create'} Media Group
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Media Group Name'}
                                placeholder='Enter a name'
                                withAsterisk
                                key={form.key('name')}
                                {...form.getInputProps('name')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'User'}
                                    placeholder='User'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    withAsterisk
                                    {...form.getInputProps('userId')}
                                />
                            ) : null}

                            <Select
                                searchable
                                label={'Category'}
                                placeholder='Select a category'
                                data={toGroupedOptions(OFFER_CATEGORIES)}
                                withAsterisk
                                {...form.getInputProps('category')}
                            />

                            <SearchableSelectCreatable
                                label={'Vertical'}
                                placeholder='Select or create a new vertical'
                                queryHttpFn={(queryParams) => {
                                    const userId = form.values['userId'];
                                    const searchUserVerticals =
                                        hasRole(userProfile, 'SUPER_ADMIN') &&
                                        userId;

                                    if (searchUserVerticals) {
                                        return getUserVerticals({
                                            userId,
                                            pagination: queryParams,
                                        });
                                    }
                                    return getVerticals(queryParams);
                                }}
                                queryKey={'verticals'}
                                optionsMapFn={(vertical) => ({
                                    label: vertical,
                                    value: vertical,
                                })}
                                {...form.getInputProps('vertical')}
                            />

                            <TextInput
                                label={'Tracking ID'}
                                placeholder='Enter a tracking id'
                                {...form.getInputProps('trackingId')}
                            />

                            <TagsInput
                                label={'Tags'}
                                placeholder={'Tags'}
                                key={form.key('tags')}
                                {...form.getInputProps('tags')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    {existing ? 'Update' : 'Create'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default CreateMediaGroupModal;
